import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Text, Button, IconButton, Input, InputGroup, InputLeftElement, Image, Skeleton, Menu, MenuButton, MenuList, MenuItem, MenuDivider,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter, useToast, Collapse,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Select, FormControl, FormLabel
} from '@chakra-ui/react';
import { Search, Phone, WhatsApp, MoreVert, ExpandMore, ExpandLess, LocalShipping, Refresh } from '@mui/icons-material';

import { Segmented } from '../components/admin/ui';
import AdminLayout from '../components/admin/AdminLayout';
import StatusBadge, { ORDER_STATUS } from '../components/admin/StatusBadge';
import { getAdminOrdersWithMeta, setAdminOrderStatus, shipWithShiprocket, trackShiprocket, resendOrderEmail } from '../services/AdminServices';
import { getOrderWhatsapp, openWhatsapp } from '../services/CommunicationsServices';
import { useUserContext } from '../contexts/UserContext';
import { inr, shortId, formatDateTime } from '../utils/format';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'onWay', label: 'On the way' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' }
];

// Stored phones are 10-digit Indian numbers or +<code><number>
const phoneDigits = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return String(phone || '').startsWith('+') ? digits : digits.length === 10 ? `91${digits}` : digits;
};

const OrdersforAdmin = () => {

  const toast = useToast();
  const { state } = useLocation();
  const { currentUser } = useUserContext();
  const [orders, setOrders] = useState(null);
  const [shiprocketOn, setShiprocketOn] = useState(false);
  const [tab, setTab] = useState(state?.status || 'all');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const cancelRef = useRef();

  useEffect(() => {
    getAdminOrdersWithMeta()
      .then(data => { setOrders(data.orders); setShiprocketOn(data.shiprocketEnabled); })
      .catch(() => setOrders([]));
  }, [currentUser]);

  useEffect(() => {
    if (state?.focus && orders) {
      setTimeout(() => document.getElementById(`order-${state.focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [state, orders]);

  const counts = useMemo(() => {
    const c = { all: orders?.length || 0, preparing: 0, onWay: 0, delivered: 0, cancelled: 0 };
    (orders || []).forEach(o => { c[o.status] += 1; });
    return c;
  }, [orders]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (orders || []).filter(o =>
      (tab === 'all' || o.status === tab) &&
      (!q || [o.buyer?.name, o.buyer?.phone, o.buyer?.email, String(o._id), shortId(o._id)].some(v => String(v || '').toLowerCase().includes(q.replace('#', ''))))
    );
  }, [orders, tab, query]);

  const changeStatus = async (order, status) => {
    setBusyId(order._id);
    try {
      await setAdminOrderStatus(currentUser, order._id, status);
      setOrders(prev => prev.map(o => o._id === order._id ? { ...o, status } : o));
      toast({ title: `Order ${shortId(order._id)}: ${ORDER_STATUS[status].label}`, status: 'success', duration: 1800, position: 'top' });
    } catch (e) {
      toast({ title: 'Could not update the order', description: e.response?.data?.error || e.message, status: 'error', duration: 3000, position: 'top' });
    }
    setBusyId(null);
  };

  const ship = async (order) => {
    setBusyId(order._id);
    try {
      const result = await shipWithShiprocket(order._id);
      setOrders(prev => prev.map(o => o._id === order._id ? { ...o, status: result.status, shiprocket: result.shiprocket } : o));
      toast({ title: result.shiprocket.awb ? `Shipped: AWB ${result.shiprocket.awb}` : 'Created on Shiprocket', description: result.shiprocket.note || result.shiprocket.courier, status: result.shiprocket.awb ? 'success' : 'warning', duration: 4000, position: 'top' });
    } catch (e) {
      toast({ title: 'Could not ship', description: e.response?.data?.error || e.message, status: 'error', duration: 5000, position: 'top' });
    }
    setBusyId(null);
  };

  const track = async (order) => {
    setBusyId(order._id);
    try {
      const result = await trackShiprocket(order._id);
      setOrders(prev => prev.map(o => o._id === order._id ? { ...o, status: result.status, shiprocket: result.shiprocket } : o));
      toast({ title: result.shiprocket.lastStatus || 'Tracking updated', status: 'info', duration: 2500, position: 'top' });
    } catch (e) {
      toast({ title: 'Could not refresh tracking', description: e.response?.data?.error || e.message, status: 'error', duration: 4000, position: 'top' });
    }
    setBusyId(null);
  };

  return (
    <AdminLayout title='Orders' subtitle={orders ? `${counts.all} total · ${counts.preparing} to ship` : 'Loading…'} >
      <InputGroup mb={3} >
        <InputLeftElement pointerEvents='none' height='44px' color='blackAlpha.600' ><Search fontSize='small' /></InputLeftElement>
        <Input bg='raano.white' height='44px' borderRadius='6px' borderColor='#D9D9D9' _hover={{ borderColor: 'blackAlpha.500' }} _focusVisible={{ borderColor: 'raano.black', boxShadow: 'none' }} type='search' placeholder='Search name, phone or order no.' value={query} onChange={(e) => setQuery(e.target.value)} />
      </InputGroup>

      <Box mb='16px' >
        <Segmented label='Order status' value={tab} onChange={setTab} options={TABS.map(t => ({ value: t.key, label: t.label, count: counts[t.key] }))} />
      </Box>

      {orders === null
        ? Array(3).fill(null).map((_, i) => <Skeleton key={i} height='180px' borderRadius='12px' mb='12px' />)
        : visible.length === 0
          ? <Box textAlign='center' py='48px' bg='raano.white' borderRadius='12px' border='1px solid' borderColor='raano.border' >
            <Text fontWeight={700} >No orders here</Text>
            <Text fontSize={14} color='blackAlpha.600' mt={1} >{query ? 'Try a different search.' : 'New orders will show up here.'}</Text>
          </Box>
          : visible.map(order => (
            <OrderCard
              key={order._id}
              order={order}
              busy={busyId === order._id}
              highlight={state?.focus === order._id}
              shiprocketOn={shiprocketOn}
              onShip={() => ship(order)}
              onTrack={() => track(order)}
              onEmail={() => setEmailFor(order)}
              onChangeStatus={(status) => status === 'cancelled' ? setConfirmCancel(order) : changeStatus(order, status)}
            />
          ))}

      <ResendEmailDialog order={emailFor} onClose={() => setEmailFor(null)} onUpdated={(id, email) => setOrders(prev => prev.map(o => o._id === id ? { ...o, buyer: { ...o.buyer, email } } : o))} />

      <AlertDialog isOpen={!!confirmCancel} leastDestructiveRef={cancelRef} onClose={() => setConfirmCancel(null)} isCentered >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4} borderRadius='12px' >
            <AlertDialogHeader fontSize={18} >Cancel order {confirmCancel && shortId(confirmCancel._id)}?</AlertDialogHeader>
            <AlertDialogBody fontSize={14} color='blackAlpha.700' >
              The order will be marked as cancelled and removed from revenue. Refunds for online payments must be issued from your Razorpay/Stripe dashboard.
            </AlertDialogBody>
            <AlertDialogFooter gap={2} >
              <Button ref={cancelRef} variant='wipe' height='40px' px='18px' fontSize='14px' onClick={() => setConfirmCancel(null)} >Keep order</Button>
              <Button height='40px' px='18px' fontSize='14px' borderRadius='8px' colorScheme='red' onClick={() => { changeStatus(confirmCancel, 'cancelled'); setConfirmCancel(null); }} >Cancel order</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AdminLayout>
  )
}

const OrderCard = ({ order, busy, highlight, onChangeStatus, shiprocketOn, onShip, onTrack, onEmail }) => {

  const [showAddress, setShowAddress] = useState(false);
  const status = ORDER_STATUS[order.status];
  const phone = phoneDigits(order.buyer?.phone);
  const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <Box id={`order-${order._id}`} bg='raano.white' borderRadius='12px' border='1px solid' borderColor={highlight ? 'raano.black' : 'raano.border'} boxShadow={highlight ? '0 0 0 3px rgba(0,0,0,.08)' : 'none'} p={{ base: '16px', md: '20px' }} mb='12px' >
      <Box display='flex' justifyContent='space-between' alignItems='flex-start' gap={2} >
        <Box minWidth={0} >
          <Text fontSize='16px' fontWeight={600} noOfLines={1} >{order.buyer?.name || 'Deleted customer'}</Text>
          <Text fontSize={12} color='blackAlpha.600' >{shortId(order._id)} · {formatDateTime(order.orderDate)}</Text>
        </Box>
        <Box display='flex' alignItems='center' gap={1} flexShrink={0} >
          <StatusBadge status={order.status} size='sm' />
          <Menu placement='bottom-end' >
            <MenuButton as={IconButton} size='sm' variant='ghost' borderRadius='full' aria-label='More actions' icon={<MoreVert fontSize='small' />} />
            <MenuList zIndex={600} fontSize={14} >
              {['preparing', 'onWay', 'delivered'].filter(s => s !== order.status).map(s => (
                <MenuItem key={s} onClick={() => onChangeStatus(s)} >Set as {ORDER_STATUS[s].label.toLowerCase()}</MenuItem>
              ))}
              <MenuItem onClick={onEmail} >Send email to customer…</MenuItem>
              {order.status !== 'cancelled' && <><MenuDivider /><MenuItem color='red.600' onClick={() => onChangeStatus('cancelled')} >Cancel order</MenuItem></>}
            </MenuList>
          </Menu>
        </Box>
      </Box>

      <Box mt={3} display='flex' flexDirection='column' gap={2} >
        {order.items.map((item, i) => (
          <Box key={`${item.id}-${i}`} display='flex' alignItems='center' gap={3} >
            <Image src={item.imageUrl} alt='' width='40px' height='52px' objectFit='cover' objectPosition='top' borderRadius='6px' bg='raano.imageBg' fallback={<Box width='40px' height='52px' borderRadius='6px' bg='raano.imageBg' />} />
            <Box flex={1} minWidth={0} >
              <Text fontSize={14} fontWeight={600} noOfLines={1} >{item.name}</Text>
              <Text fontSize={12} color='blackAlpha.600' >{item.size ? `Size ${item.size} · ` : ''}Qty {item.quantity}{item.price ? ` × ${inr(item.price)}` : ''}</Text>
            </Box>
          </Box>
        ))}
      </Box>

      <Box display='flex' justifyContent='space-between' alignItems='baseline' mt={3} pt={3} borderTop='1px solid' borderColor='raano.border' >
        <Text fontSize={13} color='blackAlpha.600' >
          {itemCount} {itemCount === 1 ? 'item' : 'items'}{order.paymentMethod && order.paymentMethod !== 'demo' ? ` · Paid via ${order.paymentMethod === 'razorpay' ? 'Razorpay' : 'Stripe'}` : ''}
        </Text>
        <Text fontSize='20px' fontWeight={700} sx={{ fontStretch: '75%' }} >{inr(order.total)}{order.estimated && <Text as='span' fontSize={11} fontWeight={600} color='blackAlpha.500' ml={1} title='Price not recorded at checkout; estimated from current prices' >est.</Text>}</Text>
      </Box>

      <Box as='button' width='100%' display='flex' alignItems='center' justifyContent='space-between' mt={2} fontSize={13} color='blackAlpha.700' onClick={() => setShowAddress(!showAddress)} >
        <Text noOfLines={showAddress ? undefined : 1} textAlign='left' >📍 {order.address}</Text>
        {showAddress ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
      </Box>
      <Collapse in={showAddress} animateOpacity >
        {order.buyer?.email && <Text fontSize={13} color='blackAlpha.600' mt={1} >✉️ {order.buyer.email}</Text>}
        {order.razorpayPaymentId && <Text fontSize={12} color='blackAlpha.500' mt={1} >Payment ID: {order.razorpayPaymentId}</Text>}
      </Collapse>

      {order.shiprocket &&
        <Box mt={3} p={3} bg='raano.surface' borderRadius='8px' display='flex' alignItems='center' gap={3} >
          <LocalShipping sx={{ fontSize: 20, color: '#2b6cb0' }} />
          <Box flex={1} minWidth={0} >
            <Text fontSize={13} fontWeight={700} noOfLines={1} >{order.shiprocket.courier || 'Shiprocket'}{order.shiprocket.awb ? ` · AWB ${order.shiprocket.awb}` : ''}</Text>
            <Text fontSize={12} color='blackAlpha.700' noOfLines={2} >{order.shiprocket.lastStatus || order.shiprocket.note || 'Pickup scheduled'}</Text>
          </Box>
          {order.shiprocket.trackingUrl && <Button as='a' href={order.shiprocket.trackingUrl} target='_blank' rel='noreferrer' size='xs' variant='wipe' height='28px' px='10px' fontSize='12px' >Track</Button>}
          {order.shiprocket.awb && <IconButton size='xs' variant='ghost' aria-label='Refresh tracking' icon={<Refresh fontSize='small' />} isLoading={busy} onClick={onTrack} />}
        </Box>}

      <Box display='flex' gap={2} mt={3} >
        {order.status === 'preparing' && shiprocketOn && !order.shiprocket
          ? <Button flex={1} variant='ink' height='40px' fontSize='15px' leftIcon={<LocalShipping fontSize='small' />} isLoading={busy} loadingText='Booking courier…' onClick={onShip} >Ship with Shiprocket</Button>
          : status.next &&
          <Button flex={1} variant='ink' height='40px' fontSize='15px' isLoading={busy} onClick={() => onChangeStatus(status.next)} >{status.nextLabel}</Button>}
        {phone && <>
          <IconButton as='a' href={`tel:+${phone}`} size='sm' height='40px' width='40px' borderRadius='8px' variant='outline' borderColor='raano.border' aria-label='Call customer' icon={<Phone fontSize='small' />} />
          <WhatsappMenu orderId={order._id} status={order.status} />
        </>}
      </Box>
    </Box>
  )
}

const ResendEmailDialog = ({ order, onClose, onUpdated }) => {
  const toast = useToast();
  const [event, setEvent] = useState('order_confirmed');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!order) return;
    setEmail(order.buyer?.email || '');
    setEvent(order.status === 'preparing' ? 'order_confirmed' : order.status === 'delivered' ? 'post_purchase' : 'order_status');
  }, [order]);

  const send = async () => {
    setSending(true);
    try {
      const result = await resendOrderEmail(order._id, event, email.trim());
      onUpdated(order._id, email.trim().toLowerCase());
      toast({
        title: result.status === 'sent' ? `Email sent to ${result.to}` : 'Email not sent',
        description: result.status === 'sent' ? undefined : result.detail,
        status: result.status === 'sent' ? 'success' : 'warning', duration: 4000, position: 'top'
      });
      if (result.status === 'sent') onClose();
    } catch (e) {
      toast({ title: 'Could not send', description: e.response?.data?.error || e.message, status: 'error', duration: 4000, position: 'top' });
    }
    setSending(false);
  };

  return (
    <Modal isOpen={!!order} onClose={onClose} isCentered >
      <ModalOverlay />
      <ModalContent mx={4} borderRadius='12px' >
        <ModalHeader fontSize={18} >Email {order?.buyer?.name || 'customer'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3} >
            <FormLabel fontSize={14} mb={1} >Message</FormLabel>
            <Select value={event} onChange={(e) => setEvent(e.target.value)} >
              <option value='order_confirmed'>Order confirmation</option>
              <option value='order_status'>Status update ({order && ORDER_STATUS[order.status].label.toLowerCase()})</option>
              <option value='post_purchase'>Thank-you & review request</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize={14} mb={1} >Send to</FormLabel>
            <Input type='email' value={email} onChange={(e) => setEmail(e.target.value)} />
            <Text fontSize={12} color='blackAlpha.600' mt={1} >Changing this updates the order's email for future updates too.</Text>
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2} >
          <Button variant='wipe' height='40px' px='18px' fontSize='14px' onClick={onClose} >Cancel</Button>
          <Button variant='ink' height='40px' px='18px' fontSize='14px' isLoading={sending} isDisabled={!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())} onClick={send} >Send email</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Sends an order template from the admin's own WhatsApp (templates live on the Communications page)
const WhatsappMenu = ({ orderId, status }) => {
  const toast = useToast();
  const send = (event) => openWhatsapp(() => getOrderWhatsapp(orderId, event).then(r => r.link))
    .catch(e => toast({ title: 'Could not open WhatsApp', description: e.response?.data?.error || e.message, status: 'error', duration: 3000, position: 'top' }));
  return (
    <Menu placement='top-end' >
      <MenuButton as={IconButton} size='sm' height='40px' width='40px' borderRadius='8px' variant='outline' colorScheme='whatsapp' aria-label='WhatsApp customer' icon={<WhatsApp fontSize='small' />} />
      <MenuList zIndex={600} fontSize={14} >
        <MenuItem onClick={() => send('order_confirmed')} >Send order confirmation</MenuItem>
        <MenuItem onClick={() => send('order_status')} >Send status update ({ORDER_STATUS[status].label.toLowerCase()})</MenuItem>
        {status === 'delivered' && <MenuItem onClick={() => send('post_purchase')} >Send thank-you & review request</MenuItem>}
      </MenuList>
    </Menu>
  );
};

export default OrdersforAdmin;
